import type { AccountType } from '@fleece/models';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Modal } from '@/components/modal';
import { DialogForm } from '@/components/settings/credential-dialogs';
import { Button, Field, Input, Segmented, Spinner } from '@/components/ui';
import { useCreateAccount } from '@/hooks/use-ledger';
import { ACCOUNT_TYPE_HINTS, accountCreateRequest, accountDraftProblem, newAccountDraft, normaliseAccountIdInput, type AccountDraft } from '@/lib/accounts';

const TYPE_OPTIONS: ReadonlyArray<{ readonly value: AccountType; readonly label: string }> = [
  { value: 'paper', label: 'Paper' },
  { value: 'live', label: 'Live' },
  { value: 'mirror', label: 'Mirror' },
];

export function CreateAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} title="Add virtual account" onClose={onClose} className="w-[min(420px,calc(100vw-32px))]">
      <CreateAccountForm onDone={onClose} />
    </Modal>
  );
}

function CreateAccountForm({ onDone }: { onDone: () => void }) {
  const [draft, setDraft] = useState<AccountDraft>(newAccountDraft);
  const create = useCreateAccount();
  const problem = accountDraftProblem(draft);
  const set = (change: Partial<AccountDraft>): void => {
    setDraft((current) => ({ ...current, ...change }));
    // The service's last answer was about the draft before this edit; leaving it up would
    // hide what is wrong with this one.
    if (create.error !== null) {
      create.reset();
    }
  };

  const submit = (): void => {
    if (problem === undefined) {
      create.mutate(accountCreateRequest(draft), { onSuccess: onDone });
    }
  };

  // Shown only once something has been typed, so an empty form does not open scolding.
  const shownProblem = draft.name.length > 0 || draft.accountId.length > 0 ? problem : undefined;

  return (
    <DialogForm
      onSubmit={submit}
      error={create.error?.message ?? shownProblem}
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={problem !== undefined || create.isPending}>
            {create.isPending ? <Spinner className="border-accent-ink/40 border-t-accent-ink" /> : <Plus className="size-3.5" />}
            Add account
          </Button>
        </>
      }
    >
      <Field label="Name" hint="letters, digits, spaces, _ and ( )">
        <Input value={draft.name} onChange={(event) => set({ name: event.target.value })} placeholder="SPY put credit spreads" maxLength={50} autoFocus />
      </Field>
      <div className="flex flex-col gap-1">
        <span className="text-[11px] text-ink-2">Type</span>
        <Segmented label="Account type" value={draft.accountType} options={TYPE_OPTIONS} onChange={(accountType) => set({ accountType })} />
        <span className="text-[11px] text-muted">{ACCOUNT_TYPE_HINTS[draft.accountType]} An account&apos;s type cannot be changed later.</span>
      </div>
      <Field label="Account id" hint="optional · 10 of 0–9 and A–Z">
        <Input
          value={draft.accountId}
          onChange={(event) => {
            const input = event.target;
            // Normalising rewrites the value, which sends the caret to the end; put it back
            // after the same characters it followed, so typing mid-id stays mid-id.
            const caret = normaliseAccountIdInput(input.value.slice(0, input.selectionStart ?? input.value.length)).length;
            set({ accountId: normaliseAccountIdInput(input.value) });
            requestAnimationFrame(() => input.setSelectionRange(caret, caret));
          }}
          placeholder="Leave empty to generate one"
          maxLength={10}
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
        />
      </Field>
    </DialogForm>
  );
}
